/**
 * 用户权限 Hook
 *
 * 使用 React Query 实现权限数据缓存和管理
 * 缓存时间：5分钟
 */

import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getUserPermissions } from '@/api/permissions';
import type { UserPermission } from '@/types/permission';
import { transformPermissions } from '@/utils/permission-transformer';

const EMPTY_PERMISSIONS: UserPermission = {};

/**
 * 用户权限 Hook
 */
export function usePermissions() {
  const queryClient = useQueryClient();

  // 使用 React Query 获取权限数据
  const query = useQuery({
    queryKey: ['permissions', 'raw'],
    queryFn: async () => {
      const result = await getUserPermissions();
      return result.permissions;
    },
    staleTime: 5 * 60 * 1000, // 5分钟
    cacheTime: 10 * 60 * 1000, // 10分钟
    select: (rawPermissions) => transformPermissions(rawPermissions),
  });

  const permissions = query.data ?? EMPTY_PERMISSIONS;

  /**
   * 检查是否有指定权限
   *
   * @param resource - 资源名称
   * @param action - 操作类型
   * @returns 是否有权限
   */
  const hasPermission = useCallback(
    (resource: string, action: string | string[]): boolean => {
      const actions = Array.isArray(action) ? action : [action];
      return actions.some((a) => permissions[resource]?.includes(a));
    },
    [permissions]
  );

  /**
   * 检查是否有任意一个权限
   *
   * @param resource - 资源名称
   * @param actions - 操作类型列表
   * @returns 是否有权限
   */
  const hasAnyPermission = useCallback(
    (resource: string, actions: string[]): boolean => {
      return actions.some((action) => permissions[resource]?.includes(action));
    },
    [permissions]
  );

  /**
   * 刷新权限（清除缓存并重新获取）
   */
  const refreshPermissions = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['permissions', 'raw'] });
  }, [queryClient]);

  return {
    permissions,
    loading: query.isLoading,
    error: query.error as Error | null,
    hasPermission,
    hasAnyPermission,
    refreshPermissions,
  };
}

export default usePermissions;
